import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { ChatNotificationProvider } from './contexts/ChatNotificationContext';
import { OfflineProvider } from './contexts/OfflineContext';
import Navigation from './components/navigation/Navigation';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/auth/LoginPage';
import LandingPage from './pages/landing/LandingPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import LeadsPage from './pages/leads/LeadsPage';
import MapsPage from './pages/maps/MapsPage';
import SeekFindPage from './pages/canvass/SeekFindPage';
import SeekFindDataPage from './pages/canvass/SeekFindDataPage';
import LeadForm from './components/leads/LeadForm';
import QuickLeadCapture from './components/leads/QuickLeadCapture';
import UserManagementPage from './pages/users/UserManagementPage';
import { SchedulePage } from './pages/schedule/SchedulePage';
import { ProjectsPage } from './pages/projects/ProjectsPage';
import ReportsPage from './pages/reports/ReportsPage';
import BillingPage from './pages/billing/BillingPage';
import InvoicesPage from './pages/billing/InvoicesPage';
import ExpensesPage from './pages/billing/ExpensesPage';
import FinancialDashboardPage from './pages/billing/FinancialDashboardPage';
import StaffPaymentsPage from './pages/billing/StaffPaymentsPage';
import ChatPage from './pages/chat/ChatPage';
import SettingsPage from './pages/settings/SettingsPage';
import WalkListsPage from './pages/walklists/WalkListsPage';
import ConsultRequestsPage from './pages/communications/ConsultRequestsPage';
import DataResetPage from './pages/admin/DataResetPage';
// Workload pages
import CreateWorkloadPage from './pages/workloads/CreateWorkloadPage';
import ManageWorkloadsPage from './pages/workloads/ManageWorkloadsPage';
import WorkloadDetailPage from './pages/workloads/WorkloadDetailPage';
import WorkloadAssignmentLogPage from './pages/workloads/WorkloadAssignmentLogPage';
import WorkloadOverviewPage from './pages/workloads/WorkloadOverviewPage';
import FollowUpRoundsPage from './pages/workloads/FollowUpRoundsPage';
// Survey pages
import SurveyManagementPage from './pages/surveys/SurveyManagementPage';
import SurveyBuilderPage from './pages/surveys/SurveyBuilderPage';
import SurveyPreviewPage from './pages/surveys/SurveyPreviewPage';
// Accountability page
import AccountabilityPage from './pages/accountability/AccountabilityPage';
// Canvass page
import CanvassPage from './pages/canvass/CanvassPage';
// Organization pages
import OrganizationsPage from './pages/organizations/OrganizationsPage';
import OrganizationDetailPage from './pages/organizations/OrganizationDetailPage';
// New feature pages
import TasksPage from './pages/tasks/TasksPage';
import ContractsPage from './pages/contracts/ContractsPage';
import TrainingPage from './pages/training/TrainingPage';
import ProjectNotesPage from './pages/projects/ProjectNotesPage';
import ProjectSchedulePage from './pages/projects/ProjectSchedulePage';
import LogisticsPage from './pages/logistics/LogisticsPage';
import EventsPage from './pages/events/EventsPage';
import PaymentHistoryPage from './pages/fiscal/PaymentHistoryPage';
import FinancialLedgerPage from './pages/fiscal/FinancialLedgerPage';
import AutomationsPage from './pages/automations/AutomationsPage';
import AppointmentsPage from './pages/appointments/AppointmentsPage';
import ActivityLogPage from './pages/activity/ActivityLogPage';
import MandatoryContractPage from './pages/contracts/MandatoryContractPage';
import { contractService } from './services/contract.service';
import ChatNotificationListener from './components/chat/ChatNotificationListener';

function App() {
  return (
    <Router>
      <AuthProvider>
        <ProjectProvider>
          <ChatNotificationProvider>
            <OfflineProvider>
              <AppContent />
              <Toaster position="top-right" />
            </OfflineProvider>
          </ChatNotificationProvider>
        </ProjectProvider>
      </AuthProvider>
    </Router>
  );
}

const AppContent: React.FC = () => {
  const location = useLocation();
  const { currentUser, loading } = useAuth();
  const [checkingContracts, setCheckingContracts] = React.useState(true);
  const [hasMandatoryContracts, setHasMandatoryContracts] = React.useState(false);

  // Check for mandatory contracts when user is logged in
  React.useEffect(() => {
    const checkMandatoryContracts = async () => {
      if (currentUser) {
        try {
          const hasContracts = await contractService.hasMandatoryUnsignedContracts(currentUser.uid);
          setHasMandatoryContracts(hasContracts);
        } catch (error) {
          console.error('Error checking mandatory contracts:', error);
        }
      }
      setCheckingContracts(false);
    };

    if (!loading) {
      checkMandatoryContracts();
    }
  }, [currentUser, loading]);

  // Show loading spinner while checking auth or contracts
  if (loading || (currentUser && checkingContracts)) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  // If on landing page and not authenticated, show landing page
  if (location.pathname === '/' && !currentUser) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
      </Routes>
    );
  }

  // If on login page, render without navigation
  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
      </Routes>
    );
  }

  // If user has mandatory unsigned contracts, show the contract signing page
  if (currentUser && hasMandatoryContracts) {
    return <MandatoryContractPage />;
  }

  // For all other pages, render inside Navigation (it expects children)
  return (
    <>
      {/* Global chat notification listener - runs on all authenticated pages */}
      <ChatNotificationListener />
      <Navigation>
      <ErrorBoundary>
      <Routes>
        <Route path="/" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="/leads" element={
          <ProtectedRoute>
            <LeadsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/leads/new" element={
          <ProtectedRoute>
            <QuickLeadCapture />
          </ProtectedRoute>
        } />

        <Route path="/leads/new/full" element={
          <ProtectedRoute>
            <LeadForm />
          </ProtectedRoute>
        } />

        <Route path="/canvass" element={
          <ProtectedRoute>
            <CanvassPage />
          </ProtectedRoute>
        } />

        <Route path="/seek-find" element={
          <ProtectedRoute>
            <SeekFindPage />
          </ProtectedRoute>
        } />

        <Route path="/seek-find/data" element={
          <ProtectedRoute allowedRoles={['client_admin', 'provider_user', 'provider_admin']}>
            <SeekFindDataPage />
          </ProtectedRoute>
        } />

        <Route path="/users" element={
          <ProtectedRoute allowedRoles={['provider_admin', 'client_admin']}>
            <UserManagementPage />
          </ProtectedRoute>
        } />

        <Route path="/schedule" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin', 'field']}>
            <SchedulePage />
          </ProtectedRoute>
        } />

        <Route path="/maps" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <MapsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/walklists" element={
          <Navigate to="/walk-lists" replace />
        } />
        
        <Route path="/projects" element={
          <ProtectedRoute>
            <ProjectsPage />
          </ProtectedRoute>
        } />

        <Route path="/projects/:projectId/schedule" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <ProjectSchedulePage />
          </ProtectedRoute>
        } />

        <Route path="/reports" element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/consult-requests" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <ConsultRequestsPage />
          </ProtectedRoute>
        } />

        <Route path="/billing" element={
          <ProtectedRoute allowedRoles={['field', 'client_admin', 'provider_admin']}>
            <BillingPage />
          </ProtectedRoute>
        } />

        <Route path="/invoices" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <InvoicesPage />
          </ProtectedRoute>
        } />

        <Route path="/expenses" element={
          <ProtectedRoute allowedRoles={['provider_admin', 'provider_user']}>
            <ExpensesPage />
          </ProtectedRoute>
        } />

        <Route path="/financials" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <FinancialDashboardPage />
          </ProtectedRoute>
        } />

        <Route path="/staff-payments" element={
          <ProtectedRoute allowedRoles={['provider_admin', 'provider_user']}>
            <StaffPaymentsPage />
          </ProtectedRoute>
        } />

        <Route path="/walk-lists" element={
          <ProtectedRoute>
            <WalkListsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/chat" element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        } />
        
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        
        <Route path="/capture" element={
          <ProtectedRoute>
            <QuickLeadCapture />
          </ProtectedRoute>
        } />

        <Route path="/admin/reset" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <DataResetPage />
          </ProtectedRoute>
        } />

        {/* Workload Routes */}
        <Route path="/workloads" element={
          <Navigate to="/workloads/manage" replace />
        } />

        <Route path="/workloads/create" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <CreateWorkloadPage />
          </ProtectedRoute>
        } />

        <Route path="/workloads/manage" element={
          <ProtectedRoute>
            <ManageWorkloadsPage />
          </ProtectedRoute>
        } />

        <Route path="/workloads/:id" element={
          <ProtectedRoute>
            <WorkloadDetailPage />
          </ProtectedRoute>
        } />

        <Route path="/workloads/log" element={
          <ProtectedRoute allowedRoles={['client_admin', 'provider_user', 'provider_admin']}>
            <WorkloadAssignmentLogPage />
          </ProtectedRoute>
        } />

        <Route path="/workloads/overview" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <WorkloadOverviewPage />
          </ProtectedRoute>
        } />

        <Route path="/workloads/follow-up" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <FollowUpRoundsPage />
          </ProtectedRoute>
        } />

        {/* Survey Routes */}
        <Route path="/surveys" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <SurveyManagementPage />
          </ProtectedRoute>
        } />

        <Route path="/surveys/builder" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <SurveyBuilderPage />
          </ProtectedRoute>
        } />

        <Route path="/surveys/builder/:id" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <SurveyBuilderPage />
          </ProtectedRoute>
        } />

        <Route path="/surveys/preview/:id" element={
          <ProtectedRoute>
            <SurveyPreviewPage />
          </ProtectedRoute>
        } />

        {/* Accountability Route */}
        <Route path="/accountability" element={
          <ProtectedRoute allowedRoles={['client_admin', 'provider_user', 'provider_admin']}>
            <AccountabilityPage />
          </ProtectedRoute>
        } />

        {/* Organization Routes (provider_admin only) */}
        <Route path="/organizations" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <OrganizationsPage />
          </ProtectedRoute>
        } />

        <Route path="/organizations/:id" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <OrganizationDetailPage />
          </ProtectedRoute>
        } />

        {/* Tasks Routes */}
        <Route path="/tasks" element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        } />

        {/* Contracts Routes */}
        <Route path="/contracts" element={
          <ProtectedRoute>
            <ContractsPage />
          </ProtectedRoute>
        } />

        {/* Training Routes */}
        <Route path="/training" element={
          <ProtectedRoute>
            <TrainingPage />
          </ProtectedRoute>
        } />

        {/* Project Notes Routes */}
        <Route path="/project-notes" element={
          <ProtectedRoute>
            <ProjectNotesPage />
          </ProtectedRoute>
        } />

        {/* Logistics Routes */}
        <Route path="/logistics" element={
          <ProtectedRoute>
            <LogisticsPage />
          </ProtectedRoute>
        } />

        {/* Events Routes */}
        <Route path="/events" element={
          <ProtectedRoute>
            <EventsPage />
          </ProtectedRoute>
        } />

        {/* Payment History Routes */}
        <Route path="/payment-history" element={
          <ProtectedRoute allowedRoles={['provider_admin', 'field']}>
            <PaymentHistoryPage />
          </ProtectedRoute>
        } />

        {/* Activity Log Route */}
        <Route path="/activity-log" element={
          <ProtectedRoute allowedRoles={['client_admin', 'provider_user', 'provider_admin']}>
            <ActivityLogPage />
          </ProtectedRoute>
        } />

        {/* Appointments Route */}
        <Route path="/appointments" element={
          <ProtectedRoute allowedRoles={['client_user', 'client_admin', 'provider_user', 'provider_admin']}>
            <AppointmentsPage />
          </ProtectedRoute>
        } />

        {/* Automations Route */}
        <Route path="/automations" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <AutomationsPage />
          </ProtectedRoute>
        } />

        {/* Financial Ledger Route */}
        <Route path="/fiscal/ledger" element={
          <ProtectedRoute allowedRoles={['provider_admin']}>
            <FinancialLedgerPage />
          </ProtectedRoute>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
    </Navigation>
    </>
  );
};

export default App;